import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import PeopleIcon from '@mui/icons-material/People';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import huLocale from '@fullcalendar/core/locales/hu';
import dayjs from 'dayjs';
import CalendarFilter from './CalendarFilter';
import { generateAutocompleteItems, generateFilter, getContrastColor } from '../../../../utils/helper';
import useUsers from '../../../../context/UsersContext';
import calendarService from '../../../../service/client/artProof/calendar.service';
import masterDataService from '../../../../service/masterData.service';
import calendarEventService from '../../../../service/calendarEvent.service';
import CalendarEventDialog from './CalendarEventDialog';
import DailyReportDialog from '../../../DailyReport/DailyReportDialog';
import dailyReportService from '../../../../service/dailyReport.service';

export const Calendar = () => {
  const { user, getUser } = useUsers();
  const calendarRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [dateRange, setDateRange] = useState({});
  const [filterVal, setFilterVal] = useState({
    resourceOneId: [],
    openedTasks: false,
  });
  const [query, setQuery] = useState({
    filter: [],
  });
  const [date, setDate] = useState({
    startDate: null,
    endDate: null,
  });
  const [autocompleteOptions, setAutocompleteOptions] = useState({
    resourceOne: [],
  });
  const [calendarEventDialogOpen, setCalendarEventDialogOpen] = useState(false);
  const [calendarEvent, setCalendarEvent] = useState({});
  const [refetch, setRefetch] = useState(false);
  const [dailyRepDialogOpen, setDailyRepDialogOpen] = useState(false);
  const [dailyReportData, setDailyReportData] = useState({});
  const [taskChanged, setTaskChanged] = useState(false);

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    initDefaultValues();
  }, []);

  useEffect(() => {
    if (date.startDate !== null && date.endDate !== null && calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();

      if (calendarApi.view.type !== 'timeGridDay') {
        calendarApi.changeView('timeGridDay', date.startDate.toDate());
      } else {
        calendarApi.gotoDate(date.startDate.toDate());
      }
    }
  }, [date.startDate, date.endDate]);

  useEffect(() => {
    if (date.startDate !== null && date.endDate !== null) {
      setDateRange(date);
    }
  }, [date.startDate, date.endDate]);

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate && query.filter.length > 0 && user.userId) {
      getAllCalendarDays();
      getAllCalendarEvents();
    }
    setTaskChanged(false);
  }, [dateRange.startDate, dateRange.endDate, query, user.userId, refetch, taskChanged]);

  const conditions = {};

  useEffect(() => {
    const defaultFilter = [
      {
        field: 'deletedBy',
        value: null,
      },
    ];
    const newUsers = filterVal.resourceOneId.map((sIds) => sIds.value);
    const newVal = { ...filterVal, resourceOneId: newUsers };
    const newFilter = generateFilter(newVal, conditions);

    if (JSON.stringify(query.filter) !== JSON.stringify([...defaultFilter, ...newFilter])) {
      setQuery((prev) => ({
        ...prev,
        filter: [...defaultFilter, ...newFilter],
      }));
    }
  }, [filterVal]);

  const initDefaultValues = () => {
    masterDataService
      .resourceOneToForm()
      .then((data) => {
        const aData = {
          resourceOne: data,
        };

        const filteredR1 = aData.resourceOne.filter((r1) => r1.active);

        const acOptions = {
          resourceOne: generateAutocompleteItems(filteredR1, 'name', 'id'),
        };
        setAutocompleteOptions(acOptions);
      })
      .finally(() => {});
  };

  const calendarViews = {
    dayGridMonth: {
      dayHeaderFormat: { weekday: 'long' },
    },
    timeGridWeek: {
      dayHeaderFormat: { month: 'long', day: 'numeric', weekday: 'long' },
      slotLabelFormat: [{ hour: 'numeric', minute: '2-digit' }],
    },
    timeGridDay: {
      slotLabelFormat: [{ hour: 'numeric', minute: '2-digit' }],
    },
  };

  const getAllCalendarDays = () => {
    const reqData = {
      query,
      dateRange,
    };
    calendarService.getCalendarDays(reqData).then((data) => {
      setEvents(data);
      setRefetch(false);
    });
  };

  const getAllCalendarEvents = () => {
    calendarEventService.getRows(dateRange).then((data) => {
      setCalendarEvents(data);
      setRefetch(false);
    });
  };

  const handleUpdateEvent = (values) => {
    const { event } = values;
    const updData = {
      startDate: dayjs(event.start),
      endDate: dayjs(event.end),
      id: parseInt(event.id),
    };

    calendarEventService.updateData(updData).then(() => {
      getAllCalendarEvents();
    });
  };

  const handleDatesSet = (dateInfo) => {
    const startDate = dayjs(dateInfo.start).startOf('day');
    const endDate = dayjs(dateInfo.end).endOf('day');

    if (dateRange.startDate?.isSame(startDate, 'day') && dateRange.endDate?.isSame(endDate, 'day')) {
      return;
    }
    setDateRange({ startDate, endDate });
  };

  const getCalendarEventById = (id) => {
    calendarEventService.getRow(id).then((data) => {
      setCalendarEvent(data);
      setCalendarEventDialogOpen(true);
    });
  };

  const getDailyReport = (id) => {
    dailyReportService.getRow(id).then((data) => {
      setDailyReportData(data);
      if (data) {
        setDailyRepDialogOpen(true);
      }
    });
  };

  return (
    <div className="calendarView flex bg-[#f3f4f6]">
      <div className="w-[350px]">
        <CalendarFilter
          filter={{ filterVal, setFilterVal }}
          dates={{ date, setDate }}
          autocompleteOptions={autocompleteOptions}
          setCalendarEventDialogOpen={setCalendarEventDialogOpen}
        />
      </div>
      <div className="w-full bg-white">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next,today',
            center: 'title',
            right: 'timeGridDay,timeGridWeek,dayGridMonth',
          }}
          locales={[huLocale]}
          views={calendarViews}
          datesSet={handleDatesSet}
          nowIndicator={true}
          dayMaxEventRows={true}
          eventClick={(eventInfo) => {
            const { event } = eventInfo;
            if (event?.extendedProps?.type === 'calendarEvent') {
              getCalendarEventById(event.id);
            } else {
              getDailyReport(event.extendedProps.baseId);
            }
          }}
          eventDrop={handleUpdateEvent}
          eventSources={[calendarEvents, events]}
          eventContent={(eventInfo) => {
            const { event } = eventInfo;
            const color = event.extendedProps?.eventColor;
            const type = event.extendedProps?.type;
            return (
              <Box display="flex" justifyContent="space-between" width="100%">
                <Box
                  sx={{
                    background: type !== 'calendarEvent' ? color : '#ADEEE3',
                    color: type !== 'calendarEvent' ? getContrastColor(color) : 'black',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flexGrow: 1,
                  }}
                >
                  {event.title}
                </Box>

                {type !== 'calendarEvent' && (
                  <Box
                    display="flex"
                    gap="0.8rem"
                    sx={{
                      background: color,
                      color: getContrastColor(color),
                    }}
                  >
                    <Box display="flex" gap="0.2rem">
                      <PeopleIcon fontSize="small" />
                      <Box>{event?.extendedProps?.countedEmployees}</Box>
                    </Box>
                    <Box display="flex" gap="0.2rem">
                      <WatchLaterIcon fontSize="small" />
                      <Box>{event?.extendedProps?.totalHours}</Box>
                    </Box>
                    <Box>{event?.extendedProps?.countedReport}</Box>
                  </Box>
                )}
              </Box>
            );
          }}
        />
      </div>
      <CalendarEventDialog
        open={calendarEventDialogOpen}
        handleClose={() => {
          setCalendarEventDialogOpen(false);
          setCalendarEvent({});
        }}
        data={calendarEvent}
        user={user}
        setRefetch={setRefetch}
      />

      <DailyReportDialog
        open={dailyRepDialogOpen}
        handleClose={() => setDailyRepDialogOpen(false)}
        task={dailyReportData}
        setTaskChanged={setTaskChanged}
      />
    </div>
  );
};
